import { useEffect, useState } from "react";
import ModalComponent from "..";
import { useAdminGetSinglePayoutDetailsQuery } from "../../../redux/features/admin/payments/payments.api";
import { useGetSinglePayoutDetailsQuery } from "../../../redux/features/payment/payment.api";
import {
  PayoutCurrency,
  PayoutDetailsResponse,
  SinglePayout,
} from "../../../redux/features/payment/payment.types";
import {
  currencyShorts,
  PRIMARY_COLOR,
  PRODUCT_PRICING,
} from "../../../utils/constants";
import { formatDate, formatToCurrency } from "../../../utils/helpers";
import { ModalCompProps } from "../../../utils/types";
import { Icon } from "../../icon/icon";
import { LoadingOval } from "../../loaders/loaders";
import { Pill } from "../../pill";
import { TableComponent } from "../../table";
import { MiniHeader } from "../../typography/miniHeader";

export const ViewPayoutModal = ({
  open,
  handleClose,
  payout,
  eventId,
  adminView = false,
  currency,
}: ModalCompProps & {
  payout: SinglePayout;
  eventId: string;
  adminView?: boolean;
  currency: PayoutCurrency;
}) => {
  const [payoutDetails, setPayoutDetails] = useState<PayoutDetailsResponse>();
  const [fetchingPayoutDetails, setFetchingPayoutDetails] =
    useState<boolean>(false);

  const { data: clientPayoutDetails, isFetching: clientFetchingPayoutDetails } =
    useGetSinglePayoutDetailsQuery(
      {
        id: eventId,
        payoutId: payout._id ?? undefined,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: adminView,
      },
    );

  const { data: adminPayoutDetails, isFetching: adminFetchingPayoutDetails } =
    useAdminGetSinglePayoutDetailsQuery(
      {
        id: eventId,
        payoutId: payout._id ?? undefined,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !adminView,
      },
    );

  useEffect(() => {
    if (adminView) {
      setFetchingPayoutDetails(adminFetchingPayoutDetails);
      setPayoutDetails(adminPayoutDetails);
    } else {
      setFetchingPayoutDetails(clientFetchingPayoutDetails);
      setPayoutDetails(clientPayoutDetails);
    }
  }, [
    clientPayoutDetails,
    clientFetchingPayoutDetails,
    adminPayoutDetails,
    adminFetchingPayoutDetails,
    adminView,
  ]);

  return (
    <ModalComponent
      open={open}
      handleClose={handleClose}
      containerClassName="md:!py-[20px] md:!px-[20px] md:!justify-end"
      className="!max-h-[90%] !rounded-t-[16px] !bg-white !p-[20px] md:!max-h-[100%] md:!max-w-[500px] md:!rounded-[16px]"
      dialogClassName="!my-0"
      showHeader
      mobileAnchorBottom
      closeOnOverlayClick
      fullScreen
      showCloseIcon
      showMobileTopBar={false}
    >
      {fetchingPayoutDetails ? (
        <div className="flex h-full w-full items-center justify-center">
          <LoadingOval color={PRIMARY_COLOR} />
        </div>
      ) : (
        <>
          <div className="flex w-full flex-col items-center justify-center gap-[2px] text-center">
            <Pill
              text={
                payoutDetails?.data?.payoutInfo?.status === "pending"
                  ? "PENDING"
                  : "PAID"
              }
              variation={
                payoutDetails?.data?.payoutInfo?.status === "pending"
                  ? "yellow"
                  : "green"
              }
            />

            <p className="text-2xl font-bold text-grey-900">
              {formatToCurrency(payoutDetails?.data?.payoutInfo?.amount ?? 0, {
                currency: currencyShorts[currency],
              })}
            </p>

            <p className="text-grey-500">
              {payoutDetails?.data?.payoutInfo?.status === "pending"
                ? "To be paid on"
                : "Paid on"}{" "}
              {formatDate(
                payoutDetails?.data?.payoutInfo?.status === "pending"
                  ? payoutDetails?.data?.payoutInfo?.payoutDate
                  : payoutDetails?.data?.payoutInfo?.payoutDate,

                "DD MMM, YYYY",
              )}
            </p>
          </div>

          <TableComponent
            headers={[]}
            rows={[
              {
                id: 1,
                items: [
                  <div className="flex flex-row items-center justify-between gap-2">
                    <div className="flex flex-1 flex-col gap-[2px]">
                      <div className="flex w-fit items-center gap-1">
                        <p className="font-bold text-primary">
                          {payout?.payoutAccount?.accountName ??
                            payout?.payoutAccount?.currency}
                        </p>

                        <Pill
                          text={
                            payout?.payoutAccount?.currency
                              ? currencyShorts[payout.payoutAccount.currency]
                              : "NA"
                          }
                          variation="grey"
                          className="!uppercase"
                        />
                      </div>

                      <p className="text-grey-500">
                        {payout?.payoutAccount?.accountNumber}{" "}
                        {payout?.payoutAccount?.bankName
                          ? `∙ ${payout?.payoutAccount?.bankName}`
                          : ""}
                      </p>
                    </div>

                    <Icon
                      svg="bank"
                      width={20}
                      height={20}
                      className="text-grey-400"
                    />
                  </div>,
                ],
              },
              {
                id: 2,
                items: [
                  <div className="flex flex-1 flex-col gap-[2px]">
                    <p className="text-grey-500">Tickets Sold</p>
                    <p className="font-bold text-primary">
                      {payoutDetails?.data?.payoutInfo?.totalTicketsSold}{" "}
                      {payoutDetails?.data?.payoutInfo?.totalTicketsSold === 1
                        ? "Ticket"
                        : "Tickets"}{" "}
                      ∙{" "}
                      {formatToCurrency(
                        payoutDetails?.data?.payoutInfo?.totalTicketValue ?? 0,
                        { currency: currencyShorts[currency] },
                      )}
                    </p>
                  </div>,
                ],
              },
              {
                id: 3,
                items: [
                  <div className="flex flex-col gap-[2px]">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-grey-500">Our Total Fee</p>

                      <Pill
                        text={
                          payoutDetails?.data?.payoutInfo?.description ??
                          `${PRODUCT_PRICING[currency].feeText} PER TICKET`
                        }
                        variation="grey"
                      />
                    </div>

                    <p className="font-bold text-primary">
                      {formatToCurrency(
                        payoutDetails?.data?.payoutInfo?.totalFees ?? 0,
                        { currency: currencyShorts[currency] },
                      )}
                    </p>
                  </div>,
                ],
              },
            ]}
            onRowClick={() => {}}
            isRowClickable={false}
            isTableEmpty={false}
            className="mt-10"
          />

          <div className="mt-5 flex flex-col gap-2">
            <MiniHeader header="History" />

            <TableComponent
              headers={[]}
              rows={
                payoutDetails?.data?.ticketSales?.map((sale, idx) => {
                  return {
                    id: idx,
                    items: [
                      <div
                        key={`${idx}`}
                        className="flex flex-col gap-[2px] text-sm text-grey-600"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="">
                            <span className="font-bold text-grey-900">
                              {formatToCurrency(sale.amount, {
                                currency: currencyShorts[currency],
                              })}
                            </span>{" "}
                            ∙{" "}
                            <span>
                              {sale.tickets
                                .map((t) => `${t.quantity}x ${t.ticketName}`)
                                .join(", ")}
                            </span>
                          </p>

                          <p className="min-w-fit">
                            {formatDate(
                              sale.purchaseDate,
                              "DD MMM, YYYY • h:mm A",
                            )}
                          </p>
                        </div>

                        <p>
                          -
                          {formatToCurrency(sale.fee, {
                            currency: currencyShorts[currency],
                          })}{" "}
                          Fee
                        </p>
                      </div>,
                    ],
                  };
                }) ?? []
              }
              onRowClick={() => {}}
              isRowClickable={false}
              isTableEmpty={false}
            />
          </div>
        </>
      )}
    </ModalComponent>
  );
};
